/**
 * @file useUpdateProfile.js
 * @module src/js/hooks/useUpdateProfile
 * @description
 * **A hook to update user profile information by calling the backend API.**
 * - Utilizes React contexts to manage user state and modal messages.
 * - Returns a function that:
 *   - Sends a PUT request to the specified endpoint with updated data.
 *   - Handles both JSON data and file uploads.
 *   - Updates the local profile data state upon successful update.
 *   - Utilizes modal messages to indicate update status.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useFetchFromBackend hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useModal } from "../contexts/ModalProvider";
import { useTheme } from "../contexts/ThemeProvider";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useUpdateProfile({
    setUpdatedProfileData,
    setUpdatedImgFiles,
    setProfileData,
    setUpdating,
    setShowInputs,
}) {
    const { showModalMessage, confirm } = useModal();
    const { theme } = useTheme();
    const fetchFromBackend = useFetchFromBackend();

    if (typeof setUpdatedProfileData !== "function" ||
        typeof setProfileData !== "function" ||
        typeof setUpdating !== "function" ||
        typeof setShowInputs !== "function"
    ) {
        throw new Error("All setter functions must be provided to useUpdateProfile.");
    }

    const updateProfile = useCallback(async ({
        request,
        addSection = false,
        deleteSection = false,
        updatedProfileData,
        updatedImgFile,
        from,
        scrollTo,
    }) => { 
        if (addSection && deleteSection) {
            throw new Error("Cannot add and delete section at the same time.");
        }
        if (!addSection && !deleteSection && updatedProfileData == null) {
            throw new Error("updatedProfileData must be provided for updating profile.");
        }


        try {
            const isAdvisorRequest = request.startsWith('/api/advisor');
            const isResearchRequest = request.startsWith('/api/research');
            const defaultState = isAdvisorRequest || isResearchRequest ? [] : {};

            if ((isAdvisorRequest || isResearchRequest) && typeof setUpdatedImgFiles !== "function") {
                throw new Error("setUpdatedImgFiles must be provided when updating advisor or research sections.");
            }

            setUpdating(true);

            if (deleteSection) {
                // Ask the user first
                const ok = await confirm({
                    message: "Are you sure you want to delete this section?\nOnly the administrator can undo this action.",
                    confirmLabel: "Delete",
                    cancelLabel: "Cancel",
                    buttonColor: theme === "dark" ? "lightcoral" : "red",
                });

                if (!ok) {
                    showModalMessage({ type: "success", content: "Cancelled." });
                    return;
                }
            }

            // User confirmed or not deleting, now perform update/add/delete
            showModalMessage({
                type: "info",
                content:
                    addSection ?
                        "Adding..." :
                    deleteSection ?
                        "Deleting..." :
                        "Updating..."
            });

            let formData = new FormData();
            let jsonData = {};
            if (!addSection && !deleteSection) {
                jsonData = {
                    ...updatedProfileData,
                    // If image is updated, use the new image name; otherwise, keep the original one
                    // Uploading image does not change updatedProfileData.photo,
                    // so updatedProfileData.photo is still the original one
                    photo: updatedImgFile != null ? updatedImgFile.name : updatedProfileData.photo
                };
                if (updatedImgFile != null) { // loose equality to catch both null and undefined
                    formData.append("file[]", updatedImgFile);
                    Object.entries(jsonData).forEach(([key, value]) => {
                        formData.append(key, value);
                    });
                }
            }

            const method = 
                addSection ?
                    "POST" :
                deleteSection ?
                    "DELETE" :
                    "PUT";

            const body =
                (addSection || deleteSection) ?
                    null :
                (updatedImgFile != null) ? // loose equality
                    formData :
                    jsonData;


            const data = await fetchFromBackend({
                request,
                method,
                body,
                uploadFile: !addSection && !deleteSection && (updatedImgFile != null),
                requireAuth: true,
                from,
                scrollTo,
            });

            if (data && data.success) {
                const data = await fetchFromBackend({ request:
                    isAdvisorRequest ? '/api/advisor' :
                    isResearchRequest ? '/api/research' : request,
                });


                setProfileData(data || defaultState);
                setUpdatedProfileData(data || defaultState);
                showModalMessage({
                    type: "success",
                    content: `Successfully ${addSection ? 'added' : deleteSection ? 'deleted' : 'updated'}!`
                });


                if (isAdvisorRequest || isResearchRequest) {
                    setUpdatedImgFiles(new Array(data.length).fill(null));
                }
                setShowInputs(isAdvisorRequest || isResearchRequest ? new Array(data.length).fill(false) : false);
                
                
            } else if (data && data.error) {
                console.error(`Failed to ${addSection ? 'add' : deleteSection ? 'delete' : 'update'}:`, data.error);
                showModalMessage({
                    type: "error",
                    content: `Failed to ${addSection ? 'add' : deleteSection ? 'delete' : 'update'}. Please try again.`
                });
            }

        } catch (error) {
            console.error(`An error occurred:`, error);
            showModalMessage({ type: "error", content: "An error occurred. Please try again." });
            
        } finally {
            setUpdating(false);
        }
    }, [
        fetchFromBackend,
        showModalMessage,
        confirm,
        theme,
        setUpdatedProfileData,
        setUpdatedImgFiles,
        setProfileData,
        setUpdating,
        setShowInputs,
    ]);

    return updateProfile;
}